import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { tomorrow } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Message } from '../types';
import './ChatMessage.css';

interface ChatMessageProps {
  message: Message;
}

const ChatMessage: React.FC<ChatMessageProps> = ({ message }) => {
  const isUser = message.role === 'user';
  const [isExpanded, setIsExpanded] = useState(false);
  
  // 计算消息行数
  const lineCount = message.content.split('\n').length;
  const shouldTruncate = isUser && lineCount > 5;
  const isTruncated = shouldTruncate && !isExpanded;
  
  // 获取显示的内容
  const getDisplayContent = () => {
    if (!isTruncated) {
      return message.content;
    }
    
    const lines = message.content.split('\n');
    return lines.slice(0, 5).join('\n') + '\n...';
  };

  const renderContent = () => {
    if (isUser) {
      // 用户消息：纯文本显示
      return (
        <div className="message-text">
          {getDisplayContent()}
          {shouldTruncate && (
            <button 
              className="expand-button"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? '收起' : `展开 (${lineCount - 5} 行)`}
            </button>
          )}
        </div>
      );
    } else {
      // AI消息：markdown渲染
      const content = message.content || '';
      return (
        <div className="message-text markdown-content">
          {content ? (
            <ReactMarkdown 
              remarkPlugins={[remarkGfm]}
              components={{
                // 自定义代码块样式
                code: ({ className, children, ...props }: any) => (
                  <MarkdownCodeBlock className={className} {...props}>{children}</MarkdownCodeBlock>
                ),
                // 自定义表格样式
                table: ({ children }) => (
                  <div className="table-wrapper">
                    <table>{children}</table>
                  </div>
                ),
                // 自定义链接样式
                a: ({ href, children }) => (
                  <a href={href} target="_blank" rel="noopener noreferrer">
                    {children}
                  </a>
                )
              }}
            >
              {content}
            </ReactMarkdown>
          ) : (
            <div className="empty-message">
              {message.isStreaming ? '正在思考中...' : '消息内容为空'}
            </div>
          )}
          {message.isStreaming && (
            <span className="streaming-indicator">▋</span>
          )}
        </div>
      );
    }
  };
  
  return (
    <div className={`message ${isUser ? 'user' : 'assistant'}`}>
      <div className="message-avatar">
        {isUser ? '👤' : '🤖'}
      </div>
      <div className="message-content">
        {renderContent()}
        <div className="message-time">
          {message.timestamp.toLocaleTimeString('zh-CN', {
            hour: '2-digit',
            minute: '2-digit'
          })}
        </div>
      </div>
    </div>
  );
};

// 新增：代码块组件，支持复制
const MarkdownCodeBlock = ({ className, children, ...props }: any) => {
  const match = /language-(\w+)/.exec(className || '');
  const isInline = !match;
  const language = match ? match[1] : '';
  const [copied, setCopied] = React.useState(false);
  const codeString = String(children).replace(/\n$/, '');
  const handleCopy = () => {
    navigator.clipboard.writeText(codeString).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    });
  };
  return !isInline ? (
    <div className="code-block-wrapper" data-language={language}>
      <button className="copy-btn" onClick={handleCopy} title={copied ? '已复制' : '复制代码'}>{copied ? '✔' : '📋'}</button>
      <SyntaxHighlighter
        style={tomorrow}
        language={language}
        PreTag="div"
        customStyle={{
          margin: 0,
          borderRadius: '6px',
          fontSize: '13px',
          lineHeight: '1.4',
          fontFamily: "'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, 'Courier New', monospace"
        }}
        codeTagProps={{
          style: {
            fontSize: '13px',
            fontFamily: "'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, 'Courier New', monospace"
          }
        }}
      >
        {codeString}
      </SyntaxHighlighter>
    </div>
  ) : (
    <code className="inline-code" {...props}>
      {children}
    </code>
  );
};

export default ChatMessage; 